import { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

import { DisplayComponent } from '@/components/DisplayComponent';
import { GenericLenderCard } from '@/components/GenericLenderCard';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import { Slogan } from '@/components/Slogan';
import { TruckstopQuickCalculatorIcons } from '@/components/TruckstopQuickCalculatorIcons';
import { EVehicleAssetType, selectVehicleAssetType } from '@/context/borrower';
import {
    EDisplays,
    selectPartner,
    selectPartnerName,
    selectPartnerNumber,
    setDisplay,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy, useFieldCopy } from '@/copy';
import { TRUCKSTOP_PARTNER_NAME } from '@/partners/truckstop';
import { AssetTypeSelector } from '@/UserInput/AssetType';
import { LoanTerm } from '@/UserInput/LoanTerm';
import { LoanUsage } from '@/UserInput/LoanUsage';
import { PurchasePrice } from '@/UserInput/PurchasePrice';
import { SelfAssessedCredit } from '@/UserInput/SelfAssessedCredit';
import { StorePersonName } from '@/UserInput/StorePersonName';
import { VehicleCondition } from '@/UserInput/VehicleCondition';
import { PHONE } from '@/utils/constants';
import {
    ELocalStorageVariables,
    getLocalStorageItem,
} from '@/utils/localStorage';

import { QuickCalculatorButton } from './QuickCalculatorButton';

export const testId = 'QuickCalculator';

export function QuickCalculator(): JSX.Element {
    // ***** LOCAL STATE *****
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [hasData, setHasData] = useState<boolean>(false);

    // ***** CONSTANTS *****
    const baseId = 'screen-quickCalculator';
    const dispatch = useAppDispatch();
    const partnerName = useAppSelector(selectPartnerName);
    const partner = useAppSelector(selectPartner);
    const partnerPhone = useAppSelector(selectPartnerNumber) || PHONE;
    const isMultiAssetJourneyEnabled = partner?.multiAssetJourney?.enabled;
    const storePersonName = partner?.storePersonName;
    const showNewOrUsedCarToggle = partner?.showNewOrUsedCarToggle;
    const isTruckstop = partnerName === TRUCKSTOP_PARTNER_NAME;
    const pathname = window.location.pathname;
    const isAssetTypePersonalLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PERSONAL_LOAN;
    const isAssetTypeBusinessLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.BUSINESS_LOAN;
    const {
        heading,
        intro,
        reassurance,
        showProgressBar,
        groupFields,
        darkEstimate,
        underCta,
        underCtaPhone,
    } = useCopy().quickCalculator;
    const fieldCopy = useFieldCopy();

    const purchaseTitle =
        isAssetTypePersonalLoan || isAssetTypeBusinessLoan
            ? 'how much are you looking to borrow?'
            : fieldCopy.purchasePrice;

    const isJayco = pathname.includes('jaycoloading');
    const authToken = getLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN);
    const firstName = getLocalStorageItem(ELocalStorageVariables.FIRST_NAME);
    const state = store.getState();
    const maritalStatus = state.borrowerReducer.personalDetails.maritalStatus;
    useEffect(() => {
        if (authToken && firstName && maritalStatus) {
            setHasData(true);
        }
    }, [authToken, firstName, maritalStatus]);

    if (isJayco) {
        return (
            <>
                <div className='flex flex-col justify-center items-center w-full'>
                    <div className='h-60 w-60 pt-40'>
                        <img src='https://driveiq-assets.s3.ap-southeast-2.amazonaws.com/vroom/jayco.svg' />
                    </div>
                    <div>
                        <h1 className='text-xl pt-16 font-semibold'>
                            FINANCIAL SERVICES
                        </h1>
                    </div>
                    <div className='flex space-x-2 pt-6'>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-1'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-2'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-3'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-4'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-5'></div>
                    </div>
                </div>
            </>
        );
    }
    const isBrandNovated = state.displayReducer.partner.isNovatedLease;

    function RenderAssetTypeSelector(): JSX.Element {
        if (!isAssetTypePersonalLoan) {
            if (isBrandNovated) {
                return <LoanUsage baseId={baseId} showErrors={false} />;
            }
            if (partner?.isScoreToAsset) {
                return <AssetTypeSelector showErrors={false} baseId={baseId} />;
            }
        }
        return <SelfAssessedCredit showErrors={false} baseId={baseId} />;
    }

    return (
        <DisplayComponent baseId={baseId} data-testid={testId}>
            <Header />
            <Slogan />
            {heading && <h1 className='text-xl font-bold pt-2'>{heading}</h1>}
            {intro && <p className='text-sm text-textBody pt-2'>{intro}</p>}
            {reassurance && (
                <div className='_flex items-center gap-2 mt-2'>
                    <span className='_flex items-center justify-center shrink-0 h-5 w-5 rounded-full bg-reassureBg'>
                        <CheckIcon
                            height={12}
                            width={12}
                            className='text-reassureText'
                            aria-hidden='true'
                        />
                    </span>
                    <p className='text-sm text-reassureText'>{reassurance}</p>
                </div>
            )}
            {showProgressBar && <ProgressBar />}
            {/* A partner can group the questions into one raised card, so the
                screen reads as a single thing to fill in rather than five
                separate controls stacked on a page. */}
            <div
                className={
                    groupFields
                        ? 'rounded-[14px] bg-white shadow-sm px-4 py-3 mt-2'
                        : ''
                }
            >
                <PurchasePrice
                    showErrors={showErrors}
                    baseId={baseId}
                    title={purchaseTitle}
                />
                <LoanTerm baseId={baseId} />
                {showNewOrUsedCarToggle && (
                    <VehicleCondition showErrors={false} baseId={baseId} />
                )}
                {storePersonName && <StorePersonName baseId={baseId} />}
                <RenderAssetTypeSelector />
            </div>

            {/* The estimate and the action it leads to are one decision, so a
                partner can draw them as one panel. */}
            <div
                className={
                    darkEstimate ? 'rounded-[14px] bg-panel p-4 mt-3' : ''
                }
            >
                <GenericLenderCard />
                {/* Inside the dark panel the button belongs against the
                    estimate. Outside it, the two are separate things and need
                    the gap the screen used to carry. */}
                <div className={darkEstimate ? 'pt-3' : 'pt-4'}>
                    <QuickCalculatorButton setShowErrors={setShowErrors} />
                </div>
            </div>
            {/* Outside the panel: inside it this sat dark-on-dark. */}
            {underCta && (
                <p className='text-xs text-textBody text-center pt-2'>
                    {underCta}
                    {underCtaPhone && ` ${partnerPhone}`}
                </p>
            )}
            {isMultiAssetJourneyEnabled && (
                <div
                    className='text-xs w-full flex justify-center text-center mt-4'
                    id={`${baseId}-button-changeAsset`}
                >
                    <span
                        className='text-gray-400 underline hover:cursor-pointer hover:text-gray-500'
                        onClick={(): void => {
                            dispatch(setDisplay(EDisplays.MULTI_ASSET_JOURNEY));
                        }}
                    >
                        Change What I&apos;m Financing
                    </span>
                </div>
            )}
            {isTruckstop && <TruckstopQuickCalculatorIcons />}
            {/* Empty div below is in place of traditional back/next buttons as display component has extra styling in place for the last element */}
            <div />
        </DisplayComponent>
    );
}
